/**
 * The isometric lattice, in screen pixels.
 *
 * Desktop draws isometric models on a triangular lattice with axes
 * `u1 = (cos30, +½)`, `u2 = (cos30, −½)` (`isometric_grid.cpp:18-30`). Any point
 * is `a·u1 + b·u2 = (cos30·(a+b), ½·(a−b))`, so with `m = a+b` and `n = a−b`,
 * **`m+n = 2a` is necessarily even**. Parity IS lattice membership — a theorem,
 * not a measurement (verified 1296/1296 across the corpus, and a genuine 50%
 * filter: both (0,0) and (1,1) residues occur, (0,1)/(1,0) never).
 *
 * Two things make the SCREEN lattice model-specific, and both come from
 * `document_header` (see `lattice` there for why an imported model must record
 * them at import rather than derive them here):
 *
 *  - **The quantum is per-model.** `grid_granularity` → `{.25, 1, 2}`
 *    (`flow_document.cpp:574`). ⚠️ **READ it, never fit it.** Measured on the real
 *    iso tutorial: the declared q=0.25 puts 10/10 vertices on-lattice, q=1 only
 *    2/10, q=2 zero. A wrong quantum is not a rounding error, it relocates
 *    everything.
 *  - **The parity PHASE flips.** Import mirrors Y about `maxRawY`, substituting
 *    `n' = n_max − n`, so the rule becomes `(m + n') ≡ n_max (mod 2)`. 3 of the 9
 *    corpus fixtures have an odd `n_max` and therefore INVERT. A hardcoded
 *    "m+n even" is wrong for a third of real models.
 *
 * Spacing is **anisotropic** — x-step `cos30·g`, y-step `½·g`. "Pitch" is not one
 * number, which is why this exposes `gx`/`gy` rather than a single value.
 *
 * ⚠️ One global lattice, deliberately. A per-block offset exists (Multi Level Data
 * Center: 199 vertices share one sub-grid residue) but is **1.38px** — below
 * perception, and block detection is fragile. Do not build for it.
 *
 * ⚠️ Off-lattice points are NORMAL and must never be "corrected". In-line devices
 * (`control_valve`, `sizing_valve`, `curve_loss`) are placed ALONG the pipe run,
 * not on the grid. This module answers "where is the nearest lattice point"; it
 * does not decide who is entitled to ask.
 */
import type { HydraulicSystem } from '@shared/hydraulic'
import type { Point } from './orthogonal-router'

const COS30 = Math.cos(Math.PI / 6)

/** `grid_granularity` → quantum, per ESI `flow_document.cpp:574`:
 *  `static double const grid_granularity_values[] = { .25, 1, 2 };` */
const GRID_GRANULARITY_VALUES: Record<string, number> = { fine: 0.25, standard: 1, coarse: 2 }

export interface IsoLattice {
  /** Screen x-step: `cos30 · q · s`. */
  gx: number
  /** Screen y-step: `½ · q · s`. */
  gy: number
  /** Required value of `(m + n) mod 2` — the post-import parity phase. */
  parity: 0 | 1
}

/**
 * The lattice this system is drawn on, or `null` when it has none.
 *
 * `null` is a real answer and the common one: orthogonal models, models imported
 * before the converter emitted `grid_settings`, models authored in Connect, and
 * isometric models whose quantum was undeterminable at import. Callers must not
 * snap when it is null — there is no fallback lattice here, and guessing one moves
 * every device onto a grid the model was never drawn on.
 *
 * ⚠️ **"Unrecoverable" would be an OVERCLAIM, and an earlier draft made it.** The
 * lattice IS fittable from an already-imported model's own geometry: `gy = gx/√3`
 * by construction, so it is ONE parameter plus the parity bit, and a blind sweep
 * (no knowledge of the answer) recovers the real tutorial's `gx` as 11.265 against
 * a true 11.264089 — 1.0001, or 0.06px of drift across the whole 608px drawing.
 * The finer candidates it also finds (0.512, 0.704 …) are sublattice aliases,
 * resolved by taking the coarsest, exactly as F12's method does.
 *
 * The reason a fit is NOT used here is evidence, not impossibility: our one iso
 * fixture is **100% on-lattice**, while the spec reports three corpus fixtures at
 * **72–77%**, where a strict fit fails outright and a robust one is needed — and
 * we have no such fixture to validate a robust fit against. Recording the truth at
 * import, where the integers are exact, needs no validation set at all. **Revisit
 * this if a legacy-model backfill is ever wanted; it is buildable, not blocked.**
 */
export function isoLatticeFor(
  // `Pick` rather than the whole system: everything here comes from the header, and
  // the narrow type lets the grid renderer subscribe to `document_header` alone
  // instead of re-running its scene function on every drag.
  system: Pick<HydraulicSystem, 'document_header'> | null | undefined,
): IsoLattice | null {
  const header = system?.document_header
  const grid = header?.grid_settings
  if (grid?.grid_type !== 'isometric') return null

  const lattice = header?.lattice
  if (!lattice) return null

  const q = grid.grid_granularity !== undefined
    ? GRID_GRANULARITY_VALUES[grid.grid_granularity]
    : undefined
  if (q === undefined) return null

  const g = q * lattice.unit_px
  if (!isFinite(g) || g <= 0) return null

  return { gx: COS30 * g, gy: 0.5 * g, parity: lattice.parity }
}

/** Is `p` already a point of `lat` (within `tol` px)? */
export function isOnIsoLattice(p: Point, lat: IsoLattice, tol = 1e-6): boolean {
  const m = p.x / lat.gx
  const n = p.y / lat.gy
  const mi = Math.round(m)
  const ni = Math.round(n)
  if (Math.abs(m - mi) * lat.gx > tol || Math.abs(n - ni) * lat.gy > tol) return false
  return (((mi + ni) % 2) + 2) % 2 === lat.parity
}

/**
 * Is the run `a → b` on one of the lattice's three bearings (±30°, ±150°, ±90°)?
 *
 * Measured from the true angle rather than index arithmetic, so it is honest about
 * points that are NEAR a bearing without being on it — `atan(gy/gx) = atan(tan30)`
 * is exactly 30° by construction.
 *
 * ⚠️ Horizontal is NOT an iso bearing. `(m, n)` and `(m+2, n)` are both lattice
 * points, so a horizontal run does connect two of them — but the lattice's line
 * families are ±30° and vertical, and Desktop's grid draws those three. A device
 * that "snapped" a run to 0° would be aligning to a line that isn't on the paper.
 */
export function isOnIsoBearing(a: Point, b: Point, lat: IsoLattice, tolDeg = 0.01): boolean {
  const dx = b.x - a.x
  const dy = b.y - a.y
  if (dx === 0 && dy === 0) return false
  const ang = (Math.atan2(dy, dx) * 180) / Math.PI
  const iso = (Math.atan(lat.gy / lat.gx) * 180) / Math.PI // 30
  const bearings = [iso, -iso, 180 - iso, -(180 - iso), 90, -90]
  return bearings.some(bear => Math.abs(((ang - bear + 540) % 360) - 180) < tolDeg)
}

/**
 * How far off the cursor a bearing-preserving point may sit and still win, in
 * lattice cells.
 *
 * **2 is chosen from a measurement, not a feel.** Swept on the real imported
 * tutorial (`gx = 11.26px`), dragging South Tank against its authored 30.00° run:
 *
 * | radius | run stays on-axis | furthest the device lands from the cursor |
 * |---|---|---|
 * | 0 (plain nearest) | 13/60 | 7.2px |
 * | 1.5 cells | 32/60 | 15.8px |
 * | **2 cells** | **39/60** | **19.6px** |
 * | 3 cells | 54/60 | 33.3px |
 * | 4 cells | 59/60 | 42.0px |
 *
 * The stopping point is not the hit-rate, it is the JUMP. Every symbol is 20×20
 * (#584), and at 2 cells the device never lands further from the cursor than its
 * own footprint — past that a snap stops feeling like a snap and starts feeling
 * like a fight, and the user has no way to insist (Connect cannot represent a
 * diagonal chain, so they cannot correct it afterwards either). 3× the plain
 * hit-rate, and a deliberate pull off-axis still succeeds.
 */
export const BEARING_MAGNET_CELLS = 2

/**
 * Snap to the lattice, PREFERRING points that keep the given runs on an iso
 * bearing.
 *
 * Why this exists: snapping the device to the lattice does not keep its PIPE
 * isometric. Two lattice points lie on a bearing only when `|Δm| = |Δn|` or
 * `Δm = 0` — about 3 lines out of a 2D field, so measured on the real tutorial
 * only **13 of 60** sampled drags left the run on-axis. Desktop has the same
 * behaviour (F5: no auto-router; D0: rubber-banded terminals) and it is harmless
 * THERE, because a Desktop user redraws the run point-to-point. **Connect has no
 * such repair**: `setEdgeWaypoints` rejects any non-orthogonal chain
 * (`model-store.ts:2638`), so a diagonal is unrepresentable and a bent run is
 * permanent. Parity in the drag, but not in the repair — so we resist the bend
 * instead of copying it.
 *
 * `hold` is the adjacent polyline point of each run that must stay on-bearing.
 * ⚠️ The CALLER passes only runs that are ALREADY on a bearing — an authored
 * off-bearing run must never be "corrected" onto the grid (F2), and an in-line
 * device legitimately sits off it.
 *
 * Never freezes: if nothing within the magnet radius satisfies every held run —
 * common with 2+ connections — this returns the plain nearest lattice point. A
 * hard constraint would leave such a device with nowhere legal to go.
 */
export function snapToIsoLatticeMagnetic(p: Point, lat: IsoLattice, hold: Point[]): Point {
  const plain = snapToIsoLattice(p, lat)
  if (hold.length === 0) return plain
  if (hold.every(h => isOnIsoBearing(h, plain, lat))) return plain

  const m0 = Math.round(p.x / lat.gx)
  const n0 = Math.round(p.y / lat.gy)
  const radius = BEARING_MAGNET_CELLS * lat.gx
  // Index span that covers `radius` on the SHORTER axis too — gy < gx, so a
  // circle of radius R reaches further in n than in m.
  const span = Math.ceil(radius / Math.min(lat.gx, lat.gy)) + 1

  let best: Point | null = null
  let bestDist = Infinity
  for (let dm = -span; dm <= span; dm++) {
    for (let dn = -span; dn <= span; dn++) {
      const m = m0 + dm
      const n = n0 + dn
      if ((((m + n) % 2) + 2) % 2 !== lat.parity) continue
      const c = { x: m * lat.gx, y: n * lat.gy }
      const dist = Math.hypot(c.x - p.x, c.y - p.y)
      if (dist > radius || dist >= bestDist) continue
      if (!hold.every(h => isOnIsoBearing(h, c, lat))) continue
      bestDist = dist
      best = c
    }
  }
  return best ?? plain
}

/**
 * The nearest point of `lat` to `p`, by true Euclidean distance.
 *
 * ⚠️ Rounding each index independently and then "fixing" parity by nudging
 * whichever index has the larger residual is NOT the nearest point: the metric is
 * anisotropic (`gx ≈ 1.73·gy`), so a one-step move costs different amounts on the
 * two axes and the cheaper repair is frequently the axis with the SMALLER
 * residual. This evaluates candidates and takes the true minimum instead. The
 * search radius is verified exhaustively against a brute-force search in the
 * tests, rather than argued for here.
 */
export function snapToIsoLattice(p: Point, lat: IsoLattice): Point {
  const m0 = Math.round(p.x / lat.gx)
  const n0 = Math.round(p.y / lat.gy)

  let best: Point = { x: m0 * lat.gx, y: n0 * lat.gy }
  let bestDist = Infinity
  for (let dm = -2; dm <= 2; dm++) {
    for (let dn = -2; dn <= 2; dn++) {
      const m = m0 + dm
      const n = n0 + dn
      if ((((m + n) % 2) + 2) % 2 !== lat.parity) continue
      const x = m * lat.gx
      const y = n * lat.gy
      const dist = (x - p.x) ** 2 + (y - p.y) ** 2
      if (dist < bestDist) {
        bestDist = dist
        best = { x, y }
      }
    }
  }
  return best
}

/**
 * Lock `cursor` to the nearest of the lattice's three bearing lines through
 * `anchor`, then snap it to the nearest whole lattice step along that bearing.
 * The result is always on a bearing from `anchor` and (when `anchor` is a lattice
 * point) is itself a lattice point of correct parity. Used to commit a mid-click
 * while drawing an isometric pipe.
 */
export function snapToIsoBearingFrom(anchor: Point, cursor: Point, lat: IsoLattice): Point {
  // Step vectors along each bearing; each keeps (m + n) even, so parity holds.
  const steps: Point[] = [
    { x: lat.gx, y: lat.gy },      // +30°, (Δm, Δn) = (1,  1)
    { x: lat.gx, y: -lat.gy },     // −30°, (Δm, Δn) = (1, −1)
    { x: 0, y: 2 * lat.gy },       // vertical, (Δm, Δn) = (0, 2)
  ]
  const rel = { x: cursor.x - anchor.x, y: cursor.y - anchor.y }
  let best: Point = anchor
  let bestPerp = Infinity
  for (const s of steps) {
    const len2 = s.x * s.x + s.y * s.y
    const t = (rel.x * s.x + rel.y * s.y) / len2 // projection in step units
    const proj = { x: t * s.x, y: t * s.y }      // closest point on the line
    const perp = Math.hypot(rel.x - proj.x, rel.y - proj.y)
    if (perp < bestPerp) {
      bestPerp = perp
      const k = Math.round(t)                    // nearest whole step
      best = { x: anchor.x + k * s.x, y: anchor.y + k * s.y }
    }
  }
  return best
}
